import { useEffect, useRef, useState } from "react";
import { useDashboardSocket, useLiveTelemetry } from "../live/DashboardSocketContext";
import {
  initialLiveTelemetryState,
  reduceTelemetryPath,
  subscribeCommand,
  unsubscribeCommand,
  STALE_LIVE_THRESHOLD_MS,
  type LiveTelemetryState,
} from "../lib/liveFrames";

/**
 * Live map path for one in-progress session.
 *
 * Subscribes to the device's high-rate telemetry on mount and unsubscribes on
 * unmount — the telemetry tier is explicit and per-device, so nothing high-rate
 * flows until a view asks. On a device change React runs the previous cleanup
 * before the next effect, which is exactly the unsubscribe(old) → subscribe(new)
 * the protocol expects.
 *
 * Only ever active for a live session. A finished session accumulates nothing
 * and loads purely over REST — so a broken live channel can never degrade
 * historical review, it can only cost you the live trail.
 *
 * The path lives here rather than in the socket because the socket has no idea
 * which session is open; it just relays frames.
 */
export function useLiveTelemetryPath(
  deviceId: string | null | undefined,
  isLive: boolean,
): LiveTelemetryState {
  const { sendCommand, connected } = useDashboardSocket();
  const telemetry = useLiveTelemetry();
  const [state, setState] = useState<LiveTelemetryState>(
    initialLiveTelemetryState,
  );
  const active = isLive && !!deviceId;

  // Subscribe for as long as this view is showing a live session. `connected`
  // is a dependency so a reconnect re-subscribes rather than going silent.
  useEffect(() => {
    if (!active || !deviceId) return;
    sendCommand(subscribeCommand(deviceId));
    return () => {
      sendCommand(unsubscribeCommand(deviceId));
    };
  }, [active, deviceId, sendCommand, connected]);

  // Drop the accumulated trail when leaving live or switching device.
  useEffect(() => {
    if (!active) setState(initialLiveTelemetryState);
  }, [active, deviceId]);

  useEffect(() => {
    if (!active || !deviceId) return;
    if (!telemetry.latest || telemetry.seq === 0) return;
    setState((prev) =>
      reduceTelemetryPath(prev, telemetry.latest!, deviceId, Date.now()),
    );
  }, [telemetry, active, deviceId]);

  return active ? state : initialLiveTelemetryState;
}

/**
 * True when a session is nominally live but its channel has gone quiet.
 *
 * Distinguishes "running" from "actually reporting": a robot that dropped off
 * mid-session still reads `in_progress` in SQL forever. `activityKey` must
 * change on every frame (the path length works) so each frame resets the clock
 * and the result heals itself if frames resume.
 */
export function useLiveDataStale(
  isLive: boolean,
  activityKey: number,
  thresholdMs: number = STALE_LIVE_THRESHOLD_MS,
): boolean {
  const lastActivityRef = useRef<number>(Date.now());
  const [stale, setStale] = useState(false);

  useEffect(() => {
    lastActivityRef.current = Date.now();
    setStale(false);
  }, [isLive, activityKey]);

  useEffect(() => {
    if (!isLive) {
      setStale(false);
      return;
    }
    const id = setInterval(() => {
      setStale(Date.now() - lastActivityRef.current >= thresholdMs);
    }, 5_000);
    return () => clearInterval(id);
  }, [isLive, thresholdMs]);

  return isLive && stale;
}
